import { Component, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PersonaService } from 'src/app/shared/services/persona.service';
import { Persona } from 'src/app/shared/models/persona.model';
import { LifeCyclePhase } from '../../models/life-cycle-phase.enum';
import { ToolType } from 'src/app/shared/models/tool-type.model';
import { ToolProductTeam } from '../../models/tool-product-team.model';
import { SelectedFilters } from '../../models/tool-selected-filter.model';
import { PersonaTooltipDirective } from 'src/app/shared/directives/persona-tooltip.directive';

@Component({
  selector: 'app-tools-sidebar-filter',
  imports: [CommonModule, NgbModule, PersonaTooltipDirective],
  templateUrl: './tools-sidebar-filter.component.html',
  styleUrl: './tools-sidebar-filter.component.scss'
})
export class ToolsSidebarFilterComponent {
  // Constants
  private readonly DEBOUNCE_TIME = 1000;
  private readonly DEFAULT_OPEN_PANELS = new Set(['personas']);

  // Services
  private readonly personaService = inject(PersonaService);

  // Inputs
  readonly personas = input<Persona[]>();
  readonly lifeCyclePhases = input<LifeCyclePhase[]>();
  readonly toolTypes = input<ToolType[]>();
  readonly productTeams = input<ToolProductTeam[]>();

  // Outputs
  readonly selectedFiltersChange = output<SelectedFilters>();

  // State management
  readonly selectedPersonas = signal<number[]>([]);
  readonly selectedLifeCyclePhases = signal<LifeCyclePhase[]>([]);
  readonly selectedToolTypes = signal<number[]>([]);
  readonly selectedProductTeams = signal<number[]>([]);
  readonly openPanels = signal<Set<string>>(this.DEFAULT_OPEN_PANELS);

  // Active persona tracking
  readonly activePersona = this.personaService.activePersona;
  private previousActivePersonaId: number | undefined = undefined;

  // Debounce management
  private readonly filterChangeSubject = new Subject<void>();

  constructor() {
    this.setupFilterDebouncing();
    this.setupActivePersonaTracking();
  }

  // Panel Management
  togglePanel(panel: string): void {
    const panels = new Set(this.openPanels());
    panels.has(panel) ? panels.delete(panel) : panels.add(panel);
    this.openPanels.set(panels);
  }

  isPanelOpen(panel: string): boolean {
    return this.openPanels().has(panel);
  }

  // Persona Management
  isPersonaDisabled(personaId: number): boolean {
    return this.activePersona()?.id === personaId;
  }

  // Life Cycle Phase Management
  getLifeCyclePhaseName(lifeCyclePhaseValue: LifeCyclePhase): string {
    return LifeCyclePhase[lifeCyclePhaseValue];
  }

  isLifeCyclePhaseSelected(lifeCyclePhase: LifeCyclePhase): boolean {
    return this.selectedLifeCyclePhases().includes(lifeCyclePhase);
  }

  // Selection Management
  toggleSelection<T extends number | string>(
    listSignal: ReturnType<typeof signal<T[]>>,
    value: T
  ): void {
    const currentValues = listSignal();
    const isSelected = currentValues.includes(value);

    const updatedValues = isSelected
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    listSignal.set(updatedValues);
    this.triggerFilterChange();
  }

  // Filter Management
  clearFilters(): void {
    this.selectedPersonas.set([]);
    this.selectedLifeCyclePhases.set([]);
    this.selectedToolTypes.set([]);
    this.selectedProductTeams.set([]);
    this.emitFilters(true);
  }

  // Private Methods
  private setupFilterDebouncing(): void {
    this.filterChangeSubject
      .pipe(debounceTime(this.DEBOUNCE_TIME))
      .subscribe(() => this.emitFilters());
  }

  private setupActivePersonaTracking(): void {
    effect(() => {
      const activePersona = this.activePersona();
      this.handleActivePersonaChange(activePersona);
    });
  }

  private handleActivePersonaChange(activePersona: Persona | undefined): void {
    const currentSelected = this.selectedPersonas();

    if (activePersona) {
      this.handleActivePersonaSet(activePersona, currentSelected);
    } else {
      this.handleActivePersonaCleared(currentSelected);
    }
  }

  private handleActivePersonaSet(activePersona: Persona, currentSelected: number[]): void {
    const activeId = activePersona.id;

    // Remove previous active persona if different
    if (this.shouldRemovePreviousPersona(activeId)) {
      const withoutPrevious = currentSelected.filter(id => id !== this.previousActivePersonaId);
      this.selectedPersonas.set([...withoutPrevious, activeId]);
      this.triggerFilterChange();
    }
    // Add new active persona if not already selected
    else if (!currentSelected.includes(activeId)) {
      this.selectedPersonas.set([...currentSelected, activeId]);
      this.triggerFilterChange();
    }

    this.previousActivePersonaId = activeId;
  }

  private handleActivePersonaCleared(currentSelected: number[]): void {
    if (this.previousActivePersonaId !== undefined) {
      const updated = currentSelected.filter(id => id !== this.previousActivePersonaId);
      this.selectedPersonas.set(updated);
      this.previousActivePersonaId = undefined;
      this.triggerFilterChange();
    }
  }

  private shouldRemovePreviousPersona(activeId: number): boolean {
    return this.previousActivePersonaId !== undefined &&
      this.previousActivePersonaId !== activeId;
  }

  private triggerFilterChange(): void {
    this.filterChangeSubject.next();
  }

  private emitFilters(isCleared = false): void {
    const filters: SelectedFilters = {
      personas: this.selectedPersonas(),
      lifeCyclePhases: this.selectedLifeCyclePhases(),
      toolTypes: this.selectedToolTypes(),
      productTeams: this.selectedProductTeams(),
      isCleared: isCleared
    };

    this.selectedFiltersChange.emit(filters);
  }
}